/**
 * Covers-by-hour pacing histogram for the reservations day-summary. Pure
 * presentational: one bar per service hour (height ∝ guests) so the shape of the
 * day reads at a glance. It leans forward - hours already served are muted, and
 * everything from "now" onward shares one solid tone so the eye lands on what's
 * still coming. A "now" line and per-shift bands annotate it. Bars fill the
 * available width (no horizontal scroll); markers are positioned proportionally.
 * Dependency-free and theme-aware, in the spirit of reports/components/charts.tsx.
 */
import { useLayoutEffect, useRef, useState } from 'react';

export type HourBar = { hour: number; guests: number };
/** A shift to annotate beneath the axis: whole-hour bounds (close exclusive)
 *  already clamped to the visible range, its name, a covers detail, and an
 *  optional per-slot cap shown on a second line. */
export type ShiftBand = {
  startHour: number;
  endHour: number;
  name: string;
  detail: string;
  cap: string | null;
};

// Horizontal inset inside each (gap-less) hour cell - this is the visual gap
// between bars. Cells stay contiguous so proportional markers land exactly.
const BAR_INSET = 6;

export default function CoversByHour({
  bars,
  nowHour,
  nowOffsetHours,
  nowLabel,
  shifts = [],
  ariaLabel,
}: {
  /** One entry per hour across the whole span; gaps carry guests: 0. */
  bars: HourBar[];
  /** Hours before this are past (muted); null on non-today (all upcoming). */
  nowHour: number | null;
  /** Hours from the left edge to "now"; null hides the marker. */
  nowOffsetHours: number | null;
  /** Text for the "now" marker (e.g. "Now"); shown only when the marker is. */
  nowLabel?: string;
  /** Service windows to mark under the axis, labelled with per-shift covers. */
  shifts?: ShiftBand[];
  ariaLabel: string;
}) {
  const max = Math.max(...bars.map((b) => b.guests), 1);
  const n = bars.length || 1;
  const originHour = bars.length ? bars[0].hour : 0;
  // Hours from the left edge → a percentage across the chart. Cells are gap-less
  // and equal, so hour k sits exactly at k/n of the width.
  const xpct = (hours: number) => `${(hours / n) * 100}%`;

  // Measure the rendered chart width so shift labels can be de-overlapped in real
  // pixels. One observer on the root; markers still position proportionally.
  const rootRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => setChartWidth(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Shift labels are absolutely positioned under their band's centre, so shifts
  // sharing (or close to) the same time window would stack on the same x and
  // overlap. Assign each label to a row (lane) with a greedy sweep: sort by left
  // edge, drop each into the first row whose last label ends before this one
  // begins, else open a new row below. Labels stack downward instead of
  // colliding - no time-axis distortion. Label widths are estimated from text
  // length; positions come from the measured chart width.
  const LABEL_LANE_H = 26;
  const LABEL_GAP = 8;
  const shiftCenter = (s: ShiftBand) => {
    const startF = (s.startHour - originHour) / n;
    const endF = (s.endHour - originHour) / n;
    // Centre under the range; a band hugging the very edge nudges inward so the
    // label never runs off the chart while staying under its own rail.
    return Math.min(Math.max((startF + endF) / 2, 0.09), 0.91);
  };
  const estimateLabelPx = (s: ShiftBand) => {
    // fontSize 10; line 1 is name + " · " + covers, the cap sits on line 2.
    const line1 = (s.name.length + 3 + s.detail.length) * 6;
    const line2 = s.cap ? s.cap.length * 5.6 : 0;
    return Math.max(line1, line2) + 4;
  };
  // Keyed by array index, not startHour - several shifts can legitimately share
  // the same window (e.g. two overlapping services), so startHour is not unique.
  const shiftLane = new Map<number, number>();
  if (chartWidth > 0 && shifts.length > 0) {
    const laid = shifts
      .map((s, i) => {
        const px = shiftCenter(s) * chartWidth;
        const half = estimateLabelPx(s) / 2;
        return { i, left: px - half, right: px + half };
      })
      .sort((a, b) => a.left - b.left || a.i - b.i);
    const laneRight: number[] = [];
    for (const item of laid) {
      let row = 0;
      while (row < laneRight.length && item.left < laneRight[row] + LABEL_GAP) row++;
      laneRight[row] = item.right;
      shiftLane.set(item.i, row);
    }
  }
  const laneCount = Math.max(1, ...Array.from(shiftLane.values()).map((r) => r + 1));

  return (
    <div ref={rootRef} role="img" aria-label={ariaLabel} style={{ margin: '4px 0 2px' }}>
      {/* paddingTop is headroom for the value labels that sit above tall bars. */}
      <div style={{ paddingTop: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            height: 70,
            position: 'relative',
            // Rounded frame on all sides; interior lines below divide it into
            // hour cells. The bottom (axis) reads a touch stronger.
            border: '1px solid var(--f7-list-item-border-color, rgba(0, 0, 0, 0.08))',
            borderBottomColor: 'var(--f7-list-item-border-color, rgba(0, 0, 0, 0.14))',
            borderRadius: 8,
          }}
        >
          {/* Faint gridlines at each hour boundary so the bars read as an
              hour-by-hour timeline. Behind the bars (zIndex 0). */}
          {Array.from({ length: Math.max(0, n - 1) }, (_, i) => (
            <div
              key={`grid-${i}`}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: xpct(i + 1),
                width: 1,
                background: 'var(--f7-list-item-border-color, rgba(0, 0, 0, 0.07))',
                zIndex: 0,
              }}
            />
          ))}
          {bars.map((b) => {
            // Cap at 74% so the value label above the tallest bar has clear
            // headroom and never overlaps the top border. (Paired with the taller
            // plot area, bars keep roughly the same on-screen height as before.)
            const h = Math.round((b.guests / max) * 74);
            const isPast = nowHour != null && b.hour < nowHour;
            return (
              <div
                key={b.hour}
                style={{
                  flex: 1,
                  position: 'relative',
                  zIndex: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: `calc(100% - ${BAR_INSET}px)`,
                    height: `${h}%`,
                    minHeight: b.guests > 0 ? 2 : 0,
                    background: 'var(--f7-theme-color, #007aff)',
                    // Past recedes; every upcoming bar shares one solid tone.
                    opacity: isPast ? 0.28 : 0.85,
                    borderRadius: '3px 3px 0 0',
                  }}
                />
              </div>
            );
          })}
          {nowOffsetHours != null && (
            // Full height of the plot frame; the label lives under the axis.
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: xpct(nowOffsetHours),
                width: 2,
                zIndex: 2,
                // Blue - the complement of the orange bars - so the marker reads
                // as a distinct divider, not another bar.
                background: 'var(--f7-color-blue, #0a84ff)',
                borderRadius: 2,
              }}
            />
          )}
          {/* Value labels on the top layer (above the now line) so a full-height
              marker never sits over a bar's count. Empty hours stay blank so
              gaps read as quiet rather than a row of zeros; past values recede. */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'flex-end',
              zIndex: 3,
              pointerEvents: 'none',
            }}
          >
            {bars.map((b) => {
              const h = Math.round((b.guests / max) * 74);
              const isPast = nowHour != null && b.hour < nowHour;
              return (
                <div key={b.hour} style={{ flex: 1, position: 'relative', height: '100%' }}>
                  {b.guests > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: `${h}%`,
                        marginBottom: 2,
                        textAlign: 'center',
                        fontSize: 9,
                        lineHeight: 1,
                        color: isPast
                          ? 'var(--f7-text-color-tertiary, #c7c7cc)'
                          : 'var(--f7-text-color-secondary, #8e8e93)',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {b.guests}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', marginTop: 4 }}>
          {bars.map((b) => (
            <div
              key={b.hour}
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: 9.5,
                color: 'var(--f7-text-color-secondary, #8e8e93)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {/* Thin the axis labels so they stay legible: every 3rd hour on a
                  dense (full-day) chart, every 2nd on a shorter one. */}
              {b.hour % (n > 14 ? 3 : 2) === 0 ? b.hour : ''}
            </div>
          ))}
        </div>
        {nowOffsetHours != null && nowLabel && (
          // "Now" text on its own line under the axis, at the marker's x, so it
          // never sits over a bar's value.
          <div style={{ position: 'relative', height: 12 }}>
            <div
              style={{
                position: 'absolute',
                left: xpct(nowOffsetHours),
                transform: 'translateX(-50%)',
                fontSize: 9,
                fontWeight: 600,
                color: 'var(--f7-color-blue, #0a84ff)',
                whiteSpace: 'nowrap',
              }}
            >
              {nowLabel}
            </div>
          </div>
        )}
        {shifts.length > 0 && (
          <>
            {/* Thin rails mark each shift's span on the axis... */}
            <div style={{ position: 'relative', height: 3, marginTop: 3 }}>
              {shifts.map((s, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: xpct(s.startHour - originHour),
                    width: xpct(s.endHour - s.startHour),
                    padding: `0 ${BAR_INSET / 2}px`,
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      height: 3,
                      borderRadius: 2,
                      background: 'var(--f7-theme-color, #007aff)',
                      opacity: 0.35,
                    }}
                  />
                </div>
              ))}
            </div>
            {/* ...and each label sits under its own shift range, centred (edge
                bands nudge inward). Labels that would collide horizontally -
                including shifts sharing the same window - are pushed to a lower
                lane instead of overlapping (see shiftLane). */}
            <div style={{ position: 'relative', height: laneCount * LABEL_LANE_H, marginTop: 3 }}>
              {shifts.map((s, i) => {
                const center = shiftCenter(s);
                const lane = shiftLane.get(i) ?? 0;
                return (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      top: lane * LABEL_LANE_H,
                      left: `${center * 100}%`,
                      transform: 'translateX(-50%)',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      fontSize: 10,
                      lineHeight: 1.2,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {/* Two tight lines: name + covers, then the cap. Compact but
                        still short enough to stay under a narrow band. */}
                    <div>
                      <span style={{ fontWeight: 600, color: 'var(--f7-theme-color, #007aff)' }}>
                        {s.name}
                      </span>
                      <span style={{ color: 'var(--f7-text-color-secondary, #8e8e93)' }}>
                        {' · '}
                        {s.detail}
                      </span>
                    </div>
                    {s.cap && (
                      <div style={{ color: 'var(--f7-text-color-tertiary, #a1a1a6)' }}>{s.cap}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
